import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp, doc, updateDoc, getDocs
} from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from '../context/UserContext';
import { useSocial } from '../context/SocialContext';
import { IoArrowBack, IoSend, IoChatbubbles, IoCreate, IoPeople } from 'react-icons/io5';
import './Messages.css';

const REACTIONS = ['❤️', '😂', '😮', '😢', '👍', '🔥'];

export default function Messages() {
  const { user } = useUser();
  const navigate = useNavigate();
  const { convId } = useParams();
  const { following, followers } = useSocial();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [friends, setFriends] = useState([]);
  const [friendSearch, setFriendSearch] = useState('');
  const [onlineStatus, setOnlineStatus] = useState({});
  const [reactionMenu, setReactionMenu] = useState(null);
  const messagesEndRef = useRef(null);
  const touchTimerRef = useRef(null);

  const mutualFriendIds = following.filter(id => followers.includes(id));

  useEffect(() => {
    if (mutualFriendIds.length === 0) {
      setFriends([]);
      return;
    }
    const loadFriends = async () => {
      try {
        const usersSnap = await getDocs(collection(db, 'users'));
        const allUsers = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setFriends(allUsers.filter(u => mutualFriendIds.includes(u.id)));
      } catch (e) {
        console.error('Erreur chargement amis:', e);
      }
    };
    loadFriends();
  }, [following, followers]);

  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', user.id)
    );
    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      data.sort((a, b) => {
        const dateA = a.lastMessageAt ? a.lastMessageAt.toDate() : new Date(0);
        const dateB = b.lastMessageAt ? b.lastMessageAt.toDate() : new Date(0);
        return dateB - dateA;
      });
      setConversations(data);
      if (convId) {
        const found = data.find(c => c.id === convId);
        if (found) setActiveConv(found);
      }
    });
    return () => unsub();
  }, [user, convId]);

  useEffect(() => {
    if (!activeConv) {
      setMessages([]);
      return;
    }
    const q = query(
      collection(db, 'conversations', activeConv.id, 'messages'),
      orderBy('createdAt', 'asc')
    );
    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMessages(data);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    });
    if (activeConv.lastSenderId && activeConv.lastSenderId !== user.id) {
      updateDoc(doc(db, 'conversations', activeConv.id), {
        ['unread_' + user.id]: 0,
      });
    }
    return () => unsub();
  }, [activeConv, user]);

  useEffect(() => {
    if (conversations.length === 0) return;
    const otherIds = [...new Set(conversations.map(c => c.participants.find(id => id !== user.id)).filter(Boolean))];
    if (otherIds.length === 0) return;
    const unsubs = otherIds.map(id =>
      onSnapshot(doc(db, 'users', id), (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setOnlineStatus(prev => ({
            ...prev,
            [id]: { isOnline: data.isOnline || false, lastSeen: data.lastSeen },
          }));
        }
      })
    );
    return () => unsubs.forEach(u => u());
  }, [conversations, user]);

  // Marquer les messages comme lus
  useEffect(() => {
    if (!activeConv || !messages.length) return;
    const unreadFromOther = messages.filter(
      m => m.senderId !== user.id && !m.readAt
    );
    unreadFromOther.forEach(msg => {
      updateDoc(doc(db, 'conversations', activeConv.id, 'messages', msg.id), {
        readAt: serverTimestamp(),
      }).catch(() => {});
    });
  }, [messages, activeConv, user]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending || !activeConv) return;
    setSending(true);
    const messageText = text.trim();
    setText('');
    await addDoc(collection(db, 'conversations', activeConv.id, 'messages'), {
      text: messageText,
      senderId: user.id,
      senderName: user.name,
      createdAt: serverTimestamp(),
    });
    const otherParticipant = activeConv.participants.find(id => id !== user.id);
    await updateDoc(doc(db, 'conversations', activeConv.id), {
      lastMessage: messageText,
      lastSenderId: user.id,
      lastMessageAt: serverTimestamp(),
      ['unread_' + otherParticipant]: (activeConv['unread_' + otherParticipant] || 0) + 1,
    });
    setSending(false);
  };

  const startConversation = async (otherUser) => {
    const existing = conversations.find(c =>
      c.participants.includes(otherUser.id) && c.participants.includes(user.id)
    );
    if (existing) {
      setActiveConv(existing);
      setShowNew(false);
      setFriendSearch('');
      return;
    }
    const convRef = await addDoc(collection(db, 'conversations'), {
      participants: [user.id, otherUser.id],
      participantNames: { [user.id]: user.name, [otherUser.id]: otherUser.name },
      lastMessage: '',
      lastSenderId: null,
      lastMessageAt: serverTimestamp(),
      ['unread_' + user.id]: 0,
      ['unread_' + otherUser.id]: 0,
      createdAt: serverTimestamp(),
    });
    setActiveConv({
      id: convRef.id,
      participants: [user.id, otherUser.id],
      participantNames: { [user.id]: user.name, [otherUser.id]: otherUser.name },
    });
    setShowNew(false);
    setFriendSearch('');
  };

  const handleReaction = async (msgId, emoji) => {
    if (!activeConv) return;
    const msgRef = doc(db, 'conversations', activeConv.id, 'messages', msgId);
    const msg = messages.find(m => m.id === msgId);
    const currentReactions = msg?.reactions || {};
    if (currentReactions[user.id] === emoji) {
      const updated = { ...currentReactions };
      delete updated[user.id];
      await updateDoc(msgRef, { reactions: updated });
    } else {
      await updateDoc(msgRef, {
        reactions: { ...currentReactions, [user.id]: emoji },
      });
    }
    setReactionMenu(null);
  };

  const handleDeleteMessage = async (msgId) => {
    if (!activeConv) return;
    const msgRef = doc(db, 'conversations', activeConv.id, 'messages', msgId);
    await updateDoc(msgRef, {
      deleted: true,
      deletedAt: serverTimestamp(),
      deletedBy: user.id,
    });
    setReactionMenu(null);
  };

  const getReactionsSummary = (reactions) => {
    if (!reactions || Object.keys(reactions).length === 0) return [];
    const counts = {};
    Object.values(reactions).forEach(emoji => {
      counts[emoji] = (counts[emoji] || 0) + 1;
    });
    return Object.entries(counts).map(([emoji, count]) => ({ emoji, count }));
  };

  const getOtherName = (conv) => {
    if (!conv.participantNames) return 'Inconnu';
    const otherId = conv.participants.find(id => id !== user.id);
    return conv.participantNames[otherId] || 'Inconnu';
  };

  const timeAgo = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return 'maintenant';
    if (seconds < 3600) return Math.floor(seconds / 60) + ' min';
    if (seconds < 86400) return Math.floor(seconds / 3600) + 'h';
    return Math.floor(seconds / 86400) + 'j';
  };

  const getOnlineInfo = (conv) => {
    const otherId = conv.participants.find(id => id !== user.id);
    const status = onlineStatus[otherId];
    if (!status) return null;
    if (status.isOnline) return { online: true, text: 'En ligne' };
    if (status.lastSeen) {
      const date = status.lastSeen.toDate ? status.lastSeen.toDate() : new Date(status.lastSeen);
      const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
      if (seconds < 300) return { online: false, text: 'Il y a quelques minutes' };
      return { online: false, text: 'Vu ' + timeAgo(status.lastSeen) };
    }
    return null;
  };

  const filteredFriends = friends.filter(f =>
    f.name.toLowerCase().includes(friendSearch.toLowerCase())
  );

  // Vue conversation active
  if (activeConv) {
    return (
      <div className="page messages-page messages-chat-view">
        <div className="messages-chat-header">
          <motion.button
            className="btn btn-ghost"
            onClick={() => { setActiveConv(null); navigate('/messages'); }}
            whileTap={{ scale: 0.9 }}
          >
            <IoArrowBack />
          </motion.button>
          <div className="messages-chat-header-info">
            <div className="messages-chat-avatar-wrap">
              <div className="messages-chat-avatar">
                {getOtherName(activeConv).charAt(0).toUpperCase()}
              </div>
              {getOnlineInfo(activeConv)?.online && (
                <span className="messages-chat-online-dot" />
              )}
            </div>
            <div>
              <h2 className="messages-chat-name">{getOtherName(activeConv)}</h2>
              {(() => {
                const info = getOnlineInfo(activeConv);
                if (!info) return null;
                return (
                  <p className={'messages-online-status ' + (info.online ? 'messages-online' : 'messages-offline')}>
                    {info.online && <span className="messages-online-dot" />}
                    {info.text}
                  </p>
                );
              })()}
            </div>
          </div>
        </div>

        <div className="messages-chat-body" onClick={(e) => {
          if (!e.target.closest('.message-reaction-menu') && !e.target.closest('.message-bubble')) {
            setReactionMenu(null);
          }
        }}>
          {messages.length === 0 && (
            <p className="messages-chat-empty">
              Commence la conversation avec {getOtherName(activeConv)} ! 🪶
            </p>
          )}
          {messages.map((msg, i) => {
            const isMe = msg.senderId === user.id;
            const reactionsSummary = getReactionsSummary(msg.reactions);
            const myReaction = msg.reactions?.[user.id];
            const isDeleted = msg.deleted;

            return (
              <motion.div
                key={msg.id}
                className={'message-bubble-wrap ' + (isMe ? 'message-wrap-mine' : 'message-wrap-theirs')}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.02 }}
              >
                <div
                  className={'message-bubble ' + (isMe ? 'message-mine' : 'message-theirs') + (isDeleted ? ' message-deleted' : '')}
                  onDoubleClick={() => !isDeleted && setReactionMenu(reactionMenu === msg.id ? null : msg.id)}
                  onTouchStart={() => {
                    if (isDeleted) return;
                    touchTimerRef.current = setTimeout(() => setReactionMenu(msg.id), 500);
                  }}
                  onTouchEnd={() => clearTimeout(touchTimerRef.current)}
                  onTouchMove={() => clearTimeout(touchTimerRef.current)}
                >
                  {isDeleted ? (
                    <p className="message-text message-deleted-text">
                      🚫 {isMe ? 'Tu as supprimé ce message' : 'Message supprimé'}
                    </p>
                  ) : (
                    <p className="message-text">{msg.text}</p>
                  )}
                  <div className="message-bottom">
                    <span className="message-time">{timeAgo(msg.createdAt)}</span>
                    {isMe && !isDeleted && (
                      <span className={'message-status ' + (msg.readAt ? 'message-status-read' : '')}>
                        {msg.readAt ? '✓✓' : '✓'}
                      </span>
                    )}
                  </div>
                </div>

                {!isDeleted && reactionsSummary.length > 0 && (
                  <div className={'message-reactions ' + (isMe ? 'message-reactions-mine' : 'message-reactions-theirs')}>
                    {reactionsSummary.map(r => (
                      <span
                        key={r.emoji}
                        className={'message-reaction-pill ' + (myReaction === r.emoji ? 'message-reaction-mine' : '')}
                        onClick={() => handleReaction(msg.id, r.emoji)}
                      >
                        {r.emoji}{r.count > 1 && <span className="message-reaction-count">{r.count}</span>}
                      </span>
                    ))}
                  </div>
                )}

                <AnimatePresence>
                  {reactionMenu === msg.id && !isDeleted && (
                    <motion.div
                      className={'message-reaction-menu ' + (isMe ? 'message-reaction-menu-mine' : 'message-reaction-menu-theirs')}
                      initial={{ opacity: 0, scale: 0.8, y: 10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.8, y: 10 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                    >
                      {REACTIONS.map(emoji => (
                        <motion.button
                          key={emoji}
                          className={'message-reaction-btn ' + (myReaction === emoji ? 'message-reaction-btn-active' : '')}
                          onClick={() => handleReaction(msg.id, emoji)}
                          whileHover={{ scale: 1.3 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          {emoji}
                        </motion.button>
                      ))}
                      {isMe && (
                        <motion.button
                          className="message-reaction-btn message-delete-btn"
                          onClick={() => handleDeleteMessage(msg.id)}
                          whileHover={{ scale: 1.3 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          🗑️
                        </motion.button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        <form className="messages-chat-input" onSubmit={handleSend}>
          <input
            className="messages-input"
            placeholder="Écrire un message..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={1000}
          />
          <motion.button
            type="submit"
            className="messages-send-btn"
            disabled={!text.trim() || sending}
            whileTap={{ scale: 0.85 }}
          >
            <IoSend />
          </motion.button>
        </form>
      </div>
    );
  }

  // Vue liste des conversations
  return (
    <div className="page messages-page">
      <div className="messages-header">
        <div>
          <h1 className="messages-title">Messages</h1>
          <p className="messages-count">{conversations.length} conversation{conversations.length > 1 ? 's' : ''}</p>
        </div>
        <motion.button
          className="messages-new-btn"
          onClick={() => setShowNew(!showNew)}
          whileTap={{ scale: 0.95 }}
        >
          <IoCreate /> Nouveau
        </motion.button>
      </div>

      <AnimatePresence>
        {showNew && (
          <motion.div
            className="messages-new-card"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className="messages-new-form">
              <p className="messages-friends-label">
                <IoPeople /> Tes amis ({mutualFriendIds.length})
              </p>
              <p className="messages-friends-desc">
                Tu peux envoyer des messages aux personnes que tu suis et qui te suivent.
              </p>

              {mutualFriendIds.length > 0 ? (
                <>
                  <input
                    className="input"
                    placeholder="Rechercher un ami..."
                    value={friendSearch}
                    onChange={e => setFriendSearch(e.target.value)}
                  />
                  <div className="messages-user-results">
                    {filteredFriends.map(f => (
                      <motion.button
                        key={f.id}
                        className="messages-user-item"
                        onClick={() => startConversation(f)}
                        whileTap={{ scale: 0.97 }}
                      >
                        <div className="messages-user-avatar" style={{
                          background: f.avatarImage
                            ? `url(${f.avatarImage}) center/cover no-repeat`
                            : f.avatarColor || 'var(--amber)'
                        }}>
                          {!f.avatarImage && f.name.charAt(0).toUpperCase()}
                        </div>
                        <span>{f.name}</span>
                      </motion.button>
                    ))}
                    {filteredFriends.length === 0 && friendSearch && (
                      <p className="messages-no-results">Aucun ami trouvé pour "{friendSearch}"</p>
                    )}
                  </div>
                </>
              ) : (
                <div className="messages-no-friends">
                  <span className="messages-no-friends-icon">👥</span>
                  <p>Tu n'as pas encore d'amis.</p>
                  <p className="messages-no-friends-hint">
                    Suis des auteurs et lecteurs depuis leur profil — quand ils te suivent en retour, vous devenez amis !
                  </p>
                  <motion.button
                    className="messages-explore-btn"
                    onClick={() => navigate('/explore')}
                    whileTap={{ scale: 0.95 }}
                  >
                    Découvrir des auteurs
                  </motion.button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="messages-list">
        {conversations.length > 0 ? (
          conversations.map((conv, i) => {
            const unread = conv['unread_' + user.id] || 0;
            return (
              <motion.div
                key={conv.id}
                className={'messages-conv-item ' + (unread > 0 ? 'messages-conv-unread' : '')}
                onClick={() => { setActiveConv(conv); navigate('/messages/' + conv.id); }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                whileHover={{ x: 4 }}
              >
                <div className="messages-conv-avatar-wrap">
                  <div className="messages-conv-avatar">
                    {getOtherName(conv).charAt(0).toUpperCase()}
                  </div>
                  {onlineStatus[conv.participants.find(id => id !== user.id)]?.isOnline && (
                    <span className="messages-conv-online-dot" />
                  )}
                </div>
                <div className="messages-conv-info">
                  <div className="messages-conv-top">
                    <h3 className="messages-conv-name">{getOtherName(conv)}</h3>
                    <span className="messages-conv-time">{timeAgo(conv.lastMessageAt)}</span>
                  </div>
                  <p className="messages-conv-preview">
                    {conv.lastSenderId === user.id && 'Toi : '}
                    {conv.lastMessage || 'Nouvelle conversation'}
                  </p>
                </div>
                {unread > 0 && (
                  <motion.span
                    className="messages-conv-badge"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                  >
                    {unread}
                  </motion.span>
                )}
              </motion.div>
            );
          })
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">💬</div>
            <p className="empty-state-text">Aucune conversation. Envoie un premier message !</p>
          </div>
        )}
      </div>
    </div>
  );
}